<script setup lang="ts">
/**
 * The inquiry form of portal and website (UC-30, ADR-093): the fields of the chosen kind
 * (`inquiry.ts`), checked with the contract through vee-validate and astro-core's
 * `toZodTypedSchema`, rendered with the pattern library's controls. With more than one kind the
 * sender picks it first, in a segment as in the design prototype (B-123): radio buttons, since it
 * is exactly one of several, which the keyboard walks with the arrow keys (UP5). The app may
 * preselect a kind (`kind`) or bind it (`v-model:kind`), e.g. to retitle its dialog; unbound the
 * form keeps the choice itself (UP12). The slot `intro` sits below the segment, for the app's text
 * on the chosen kind (B-124). Fields read in the size of the text around them (16 pixels on touch
 * screens, so phones do not zoom in), under bold labels; the fields one may leave empty say
 * "(optional)" in normal weight after the label (generated content from the stylesheet, which
 * screen readers read along and a content security policy leaves alone, UP7) and the required
 * ones carry no asterisk, as in the prototype (they stay `required` for assistive technology).
 * Segment and buttons are 40 pixels high, 44 on touch screens (UP2). The send button carries the
 * brand's fill, and with `cancelable` "Abbrechen" stands beside it and emits `cancel` (B-132). The
 * app passes the sending (`send`) and decides what a failure says, except a `400` that names fields
 * the form shows: it marks those with their own error (`fields`). The form adds honeypot and `startedAt` to an `access` inquiry (ADR-050 stage 1). Errors show once a
 * field was left or a send was tried, as in astro-core's `FormIsland`. Labels, placeholders,
 * button and thanks follow the kind and the entry (`lead`), as in the start version of the design
 * prototype; the privacy sentence links to the app's privacy page when it names one. After
 * sending the form gives way to its thanks, a green box with a check, and emits `sent`; the dialog or page around it, with
 * its heading (`inquiryHeading`), stays the app's. The thanks take the focus, which would
 * otherwise fall to the page with the form that is gone (GU4, WCAG 2.4.3).
 */
import { computed, nextTick, onMounted, ref, useId, useTemplateRef } from "vue";
import { Field, useForm } from "vee-validate";
import { useI18n } from "vue-i18n";
import { toZodTypedSchema } from "@pixelpoint/astro-core/form/core/zodAdapter.ts";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import PixSelect from "@pixelpoint-pattern-library-v2/base/components/styled/base/inputs/PixSelect/PixSelect.vue";
import PixTextArea from "@pixelpoint-pattern-library-v2/base/components/styled/base/inputs/PixTextArea/PixTextArea.vue";
import PixTextField from "@pixelpoint-pattern-library-v2/base/components/styled/base/inputs/PixTextField/PixTextField.vue";
import DuotoneIcon from "./DuotoneIcon.vue";
import { inquiryKindIcons } from "./icons.ts";
import {
  inquiryBody,
  inquiryButtonTexts,
  inquiryErrorKey,
  inquiryFields,
  inquiryValueSchemas,
  NO_PRODUCT,
  type FormInquiryKind,
  type InquiryLead,
  type InquiryProductOption,
  type InquirySend,
} from "./inquiry.ts";

const props = defineProps<{
  /** The kinds the sender may choose from, the first preselected; one kind shows no choice. */
  kinds: readonly [FormInquiryKind, ...FormInquiryKind[]];
  send: InquirySend;
  /** Products to pick from; without them the form has no product field. */
  products?: InquiryProductOption[] | undefined;
  /** The product preselected, e.g. on a product page. */
  productId?: string | undefined;
  /** Where the sender comes from, when that says more than the kind (`InquiryLead`). */
  lead?: InquiryLead | undefined;
  /** The app's privacy page; without it the privacy sentence has no link. */
  privacyHref?: string | undefined;
  /** Shows "Abbrechen" beside the send button; the app closes on `cancel`. */
  cancelable?: boolean | undefined;
}>();

const emit = defineEmits<{ sent: []; cancel: [] }>();

/**
 * The kind preselected instead of the first (`kind`), or bound with `v-model:kind`; each choice
 * is reported as `update:kind`. Unbound, the form keeps the choice itself.
 */
const model = defineModel<FormInquiryKind | undefined>("kind");

const { t } = useI18n();

/** The kind on screen: the app's when it is one of `kinds`, otherwise the first; a choice sets it. */
const kind = computed({
  get: (): FormInquiryKind => (model.value && props.kinds.includes(model.value) ? model.value : props.kinds[0]),
  set: (value: FormInquiryKind) => {
    model.value = value;
  },
});
const fields = computed(() => inquiryFields(kind.value, t, props.products ?? [], props.lead));
const texts = computed(() => inquiryButtonTexts(kind.value, t));

/** The options of the segment with their icons (`icons.ts`); `access` stands alone, no icon. */
const kindOptions = computed(() => props.kinds.map((value) => ({ value, icon: inquiryKindIcons[value] })));
/** One name for the segment's radio buttons, unique on the page. */
const kindName = useId();

/** The checked option filled dark, the others recessed; whole class names (AGENTS.md). */
const kindClass = {
  on: "bg-solid text-solid-fg",
  off: "bg-fill text-text-body hover:bg-fill-2",
};

/** The prototype's buttons (`btn`): 40 pixels, 44 on touch screens, 14 pixels semibold, the control radius. */
const buttonBase =
  "text-ui min-h-(--size-field) px-4 font-semibold pointer-coarse:min-h-(--size-button) [--pix-button-border-hover:transparent] [--pix-button-border:transparent] [--pix-button-radius:var(--radius-control)]";
/**
 * Send (`btn-brand`): the brand's fill, a little brighter under the pointer. The fill starts at the
 * outer edge (`bg-origin-border`): from the padding edge it would repeat under the transparent
 * border and show its light end as a thin line on the left.
 */
const sendClass =
  "bg-origin-border [--pix-button-bg:var(--gradient-brand-fill)] [--pix-button-bg-hover:var(--gradient-brand-fill)] [--pix-button-text:var(--color-on-brand)] [--pix-button-text-hover:var(--color-on-brand)] hover:brightness-110";
/** Cancel (`btn-soft`): recessed. */
const cancelClass =
  "[--pix-button-bg:var(--color-fill)] [--pix-button-bg-hover:var(--color-fill-2)] [--pix-button-text:var(--color-text-head)] [--pix-button-text-hover:var(--color-text-head)]";

const { handleSubmit, isSubmitting, submitCount, setFieldError } = useForm({
  validationSchema: computed(() => toZodTypedSchema(inquiryValueSchemas[kind.value])),
  initialValues: { productId: props.productId ?? NO_PRODUCT },
});

// Bot protection of `access` (ADR-050): a field people never see, and when the form was shown —
// on the client, so a page rendered ahead of time does not date it.
const website = ref("");
const startedAt = ref("");
onMounted(() => {
  startedAt.value = new Date().toISOString();
});

const sent = ref(false);
const thanks = useTemplateRef<HTMLElement>("thanks");
const failure = ref("");
// One inquiry at a time: a second submit (Enter in a field) while the first is on its way is dropped.
let sending = false;

/** Marks the fields the API refused that the form shows, each with its own error; whether there was one. */
function markRefused(refused: readonly string[] = []): boolean {
  const shown = refused.filter((name) => fields.value.some((field) => field.identifier === name));
  for (const name of shown) setFieldError(name, name);
  return shown.length > 0;
}

const onSubmit = handleSubmit(async (values) => {
  if (sending) return;
  failure.value = "";
  const body = inquiryBody(
    kind.value,
    values as Record<string, unknown>,
    { website: website.value, startedAt: startedAt.value },
    props.productId,
  );
  if (!body) {
    failure.value = t("inquiryForm.failed");
    return;
  }
  sending = true;
  try {
    const result = await props.send(body);
    if (result.ok) {
      sent.value = true;
      emit("sent");
      await nextTick();
      thanks.value?.focus();
    } else {
      failure.value = markRefused(result.fields) ? t("inquiryForm.invalid") : result.message;
    }
  } finally {
    sending = false;
  }
});

/** The field's error in the sender's language, once it was left or a send was tried. */
const errorOf = (identifier: string, message: string | undefined, touched: boolean) =>
  message && (touched || submitCount.value > 0) ? t(inquiryErrorKey(identifier)) : "";

const textType = (type: string) => (type === "email" || type === "tel" ? type : "text");
</script>

<template>
  <p
    v-if="sent"
    ref="thanks"
    role="status"
    tabindex="-1"
    class="rounded-control bg-ok-bg text-ui text-text-body focus-visible:outline-focus m-0 flex items-start gap-2.5 px-3.5 py-3 focus-visible:outline-2 focus-visible:outline-offset-2"
    data-inquiry-sent
  >
    <!-- Phosphor "check-circle" of the prototype's thanks (B-133), without the duotone's tinted area -->
    <svg class="text-ok-text mt-px size-5 flex-none" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
      <path
        d="M173.66 98.34a8 8 0 0 1 0 11.32l-56 56a8 8 0 0 1-11.32 0l-24-24a8 8 0 0 1 11.32-11.32L112 148.69l50.34-50.35a8 8 0 0 1 11.32 0M232 128A104 104 0 1 1 128 24a104.11 104.11 0 0 1 104 104m-16 0a88 88 0 1 0-88 88a88.1 88.1 0 0 0 88-88"
      />
    </svg>
    <span>{{ texts.sent }}</span>
  </p>
  <form
    v-else
    class="flex flex-col gap-4 [--pix-field-font-size:var(--text-ui)] [--pix-field-label-weight:600] pointer-coarse:[--pix-field-font-size:var(--text-body)] [&_[data-field]_label:not([data-required])::after]:font-normal [&_[data-field]_label:not([data-required])::after]:content-['_(optional)'] [&_label[data-required]::after]:content-none"
    novalidate
    data-inquiry-form
    @submit="onSubmit"
  >
    <!-- the radio button sits inside its label, out of sight; the label is the segment's surface
         and shows the focus the keyboard gives the radio button. Not a `data-field`: the mark
         "(optional)" goes after the labels of the fields only -->
    <div
      v-if="props.kinds.length > 1"
      role="radiogroup"
      :aria-label="t('inquiryForm.kind.label')"
      class="grid auto-cols-fr grid-flow-col gap-2"
      data-kinds
    >
      <label
        v-for="option in kindOptions"
        :key="option.value"
        :class="[
          'rounded-control text-ui has-focus-visible:outline-focus relative flex min-h-(--size-field) cursor-pointer items-center justify-center gap-2 px-3 font-semibold transition-colors has-focus-visible:outline-2 has-focus-visible:outline-offset-2 pointer-coarse:min-h-(--size-button)',
          kind === option.value ? kindClass.on : kindClass.off,
        ]"
        :data-kind="option.value"
      >
        <input v-model="kind" type="radio" class="sr-only" :name="kindName" :value="option.value" />
        <DuotoneIcon v-if="option.icon" :icon="option.icon" class="size-4 flex-none" />
        {{ t(`inquiryForm.kind.${option.value}`) }}
      </label>
    </div>
    <slot name="intro" :kind="kind" />
    <Field
      v-for="field in fields"
      :key="field.identifier"
      v-slot="{ value, handleChange, handleBlur, errorMessage, meta }"
      :name="field.identifier"
    >
      <PixTextArea
        v-if="field.type === 'textarea'"
        :model-value="(value as string | undefined) ?? ''"
        :label="field.label"
        :placeholder="field.placeholder"
        :required="field.required"
        :error="errorOf(field.identifier, errorMessage, meta.touched)"
        :rows="5"
        :data-field="field.identifier"
        @update:model-value="handleChange"
        @blur="handleBlur"
      />
      <PixSelect
        v-else-if="field.type === 'select'"
        :model-value="(value as string | undefined) ?? NO_PRODUCT"
        :items="field.options ?? []"
        :label="field.label ?? ''"
        :placeholder="t('inquiryForm.choose')"
        :error="errorOf(field.identifier, errorMessage, meta.touched)"
        :data-field="field.identifier"
        @update:model-value="handleChange"
      />
      <PixTextField
        v-else
        :model-value="(value as string | undefined) ?? ''"
        :type="textType(field.type)"
        :label="field.label ?? ''"
        :placeholder="field.placeholder ?? ''"
        :required="field.required"
        :error="errorOf(field.identifier, errorMessage, meta.touched)"
        :data-field="field.identifier"
        @update:model-value="handleChange"
        @blur="handleBlur"
      />
    </Field>
    <!-- Honeypot (ADR-050): out of sight, out of the tab order and hidden from screen readers. -->
    <div v-if="kind === 'access'" class="sr-only" aria-hidden="true">
      <label>
        Website
        <input v-model="website" type="text" name="website" tabindex="-1" autocomplete="off" />
      </label>
    </div>
    <p class="text-meta text-text-soft m-0" data-inquiry-privacy>
      {{ t("inquiryForm.privacy.text") }}
      <template v-if="props.privacyHref">
        {{ t("inquiryForm.privacy.more") }}
        <a
          :href="props.privacyHref"
          class="rounded-chip text-accent-text focus-visible:outline-focus font-semibold underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
          >{{ t("inquiryForm.privacy.link") }}</a
        >.
      </template>
    </p>
    <p v-if="failure" role="alert" class="text-ui text-bad-text m-0" data-inquiry-failure>{{ failure }}</p>
    <div class="mt-1 flex flex-wrap justify-end gap-2 max-sm:flex-col-reverse" data-inquiry-actions>
      <PixButton
        v-if="props.cancelable"
        type="button"
        :class="[buttonBase, cancelClass]"
        data-action="cancel"
        @click="emit('cancel')"
      >
        {{ t("inquiryForm.cancel") }}
      </PixButton>
      <PixButton type="submit" :class="[buttonBase, sendClass]" :disabled="isSubmitting" data-action="send">
        {{ texts.submit }}
      </PixButton>
    </div>
  </form>
</template>
